import _ from 'underscore';
import {
  RECOMMENDATION_TYPES,
  VISUAL_TYPES
} from './consts';

import { generateSchema } from './schema';

import {
  formatEstimates,
  replacePlaceholders,
  formatSummary,
  buildSimpleFormFields,
  formatVisualDataChart,
} from './formatter/index';
import { recommendationConfigs } from './recommendation-configs';

function getModelForInsight({
  i18n, level, type,
  recommendation,
}) {
  const config = recommendationConfigs[type];

  // visual
  let { visualData } = recommendation;
  const visualType = config.recommendation.visualType || config.visualType;
  visualData = formatVisualDataChart(i18n, visualType, visualData);
  if (visualType === VISUAL_TYPES.TABLE) {
    visualData = buildSimpleFormFields(
      config.recommendation.visualData,
      _.first(recommendation.opportunities),
      i18n
    );
  }

  const insightTitle = replacePlaceholders({
    model: recommendation,
    i18n,
    level,
    type,
  }, config.recommendation.insightTitle);

  return {
    insightTitle,
    visualData,
    visualType,
  };
}

function getModelForFormAction({
  type,
  i18n,
  recommendation,
  currency,
  appConfig,
}) {
  const recommendationConfig = recommendationConfigs[type].recommendation;

  let labelProcessor;
  if (type === RECOMMENDATION_TYPES.BUDGET) {
    labelProcessor = label => label.replace('{{currency}}', currency);
  }
  const model = _.first(recommendation.opportunities);
  const fields = buildSimpleFormFields(
    recommendationConfig.actionDataInForm,
    model,
    i18n,
    labelProcessor,
    currency
  );

  const estimates = formatEstimates(_.extend(
    {},
    {
      type,
      i18n,
      currency,
      inDetailsView: true,
    },
    recommendation
  ));
  return {
    actionDataInForm: _.extend({ id: _.uniqueId('action-form-data-') }, {
      fields,
      model,
      target: recommendation.target,
      schema: generateSchema(appConfig),
    }),
    estimates,
  };
}

function getModelForGridAction({
  type,
  i18n,
  recommendation,
  currency,
}) {
  const recommendationConfig = recommendationConfigs[type].recommendation;

  const {
    dataBuilder, columns, singleSelection, helpTopics, hasLastRowAsCurrent,
  } = recommendationConfig.actionGrid;
  const localizedHeaders = _.map(_.pluck(columns, 'header'), header => (i18n.getString(header)));
  const selectedRowIndex = _.findIndex(
    recommendation.opportunities,
    opportunity => opportunity.isDefault
  );
  const bodies = dataBuilder({
    recommendation,
    i18n,
    currency,
  });

  const actionDataInGrid = {
    id: _.uniqueId('action-grid-data-'),
    headers: localizedHeaders,
    bodies,
    selectedRow: selectedRowIndex.toString(),
    singleSelection,
    helpTopics,
    columns,
    hasLastRowAsCurrent,
    model: recommendation.opportunities,
    target: recommendation.target,
  };

  return {
    actionDataInGrid,
  };
}

const getModelForAllRecommendations = (options) => {
  const {
    recommendations,
    type,
    i18n,
    level,
    currency,
    appConfig,
  } = options;
  const recommendationConfig = recommendationConfigs[type].recommendation;
  const result = _.map(recommendations, (recommendation) => {
    const data = {};

    // insight
    _.extend(data, getModelForInsight({
      i18n, level, type, recommendation,
    }));

    // action
    data.actionTitle = replacePlaceholders({
      model: recommendation,
      i18n,
      level,
      type,
    }, recommendationConfig.actionTitle);

    const commonOptionsForAction = {
      type, i18n, recommendation, currency,
    };
    if (recommendationConfig.actionDataInForm || recommendation.actionDataInForm) {
      _.extend(data, getModelForFormAction(_.extend({}, { appConfig }, commonOptionsForAction)));
    }
    if (recommendationConfig.actionGrid) {
      _.extend(data, getModelForGridAction(commonOptionsForAction));
    }
    if (_.isFunction(recommendationConfig.postprocessor)) {
      recommendationConfig.postprocessor(data, recommendation, i18n);
    }
    return data;
  });
  return result;
};

export const getDetailsSummary = (options) => {
  const summary = formatSummary(options);
  return {
    summary,
  };
};

export const getDetailsViewModel = (options) => {
  const summary = formatSummary(options);
  const recommendations = getModelForAllRecommendations(options);

  return _.extend({}, {
    summary,
    recommendations,
  });
};
